import { ColumnDef } from '@tanstack/react-table';

import { useCountries } from '@/hooks';

import { Chip, Icon, Typography } from '@/components';
import { customRound, europeanFormatNumber, orderTechTransactionIcons } from '@/helpers';
import { IconName } from '@/components/icons/types';
import { MyOffersLocation, Technology, Step, StepKey } from './types';
import { OfferItemList } from '@/features/offers/types';

const STATUS_STEPS: Step[] = [
  { key: 'needs_validation', label: 'Under Review', color: 'disabled', icon: 'clock' },
  { key: 'pending', label: 'Pending', color: 'warning', icon: 'clock' },
  { key: 'accepted', label: 'Accepted', color: 'success', icon: 'circle-check' },
  { key: 'archived', label: 'Archived', color: 'disabled' },
  { key: 'internal_rejected', label: 'Rejected', color: 'error', icon: 'circle-x' },
  { key: 'rejected', label: 'Rejected', color: 'error', icon: 'circle-x' },
];

const normalizedStep = (status: string) => {
  const STEP_BY_KEY: Record<StepKey, Step> = Object.fromEntries(STATUS_STEPS.map((s) => [s.key, s])) as Record<
    StepKey,
    Step
  >;
  const key: StepKey | undefined = status as StepKey;
  return key ? STEP_BY_KEY[key] : undefined;
};

const TechnologyIcons = ({ technologies }: { technologies: Technology }) => {
  const types = technologies.types;
  return (
    <div className="flex">
      {orderTechTransactionIcons(types || []).map((technology, i) => (
        <Icon name={technology as IconName} className="text-primary-700 mr-2 h-6 w-6" key={`${technology}-${i}`} />
      ))}
    </div>
  );
};

const CapacityDisplay = ({ capacity, hasPV }: { capacity: number; hasPV: boolean }) => {
  const roundedCapacity = customRound(capacity);
  const formattedCapacity = europeanFormatNumber(roundedCapacity);
  const capacityToDisplay = capacity === 0 ? '' : hasPV ? `${formattedCapacity} MWp` : `${formattedCapacity} MW`;
  return (
    <div className="max-w-lg min-w-0 leading-relaxed break-words whitespace-normal">
      <Typography as="p" size="body-2" weight="normal" className="text-left">
        {capacityToDisplay}
      </Typography>
    </div>
  );
};

const LocationDisplay = ({ location }: { location: MyOffersLocation[] }) => {
  const { options: countries } = useCountries();
  const transactionCountries: string[] = location.flatMap((location) =>
    Array.isArray(location.country) ? location.country : [location.country]
  );
  const countryLabels = transactionCountries.map((tc) => countries.find((c) => c.value === tc)?.label || tc).join(', ');
  return (
    <div className="max-w-lg min-w-0">
      <Typography as="p" size="body-2" weight="normal" className="text-left">
        {countryLabels}
      </Typography>
    </div>
  );
};

const OpportunitiesDisplay = ({ opportunityName }: { opportunityName: string }) => {
  const formattedName: string = opportunityName ?? '-';
  return (
    <div className="relative max-w-lg min-w-0 pt-0 leading-relaxed break-words">
      <div className={'flex flex-col pt-1'}>
        <Typography as="p" size="body-2" weight="semibold" className="text-left">
          {formattedName}
        </Typography>
      </div>
    </div>
  );
};

const StatusBadge = ({ status }: { status: string }) => {
  const step = normalizedStep(status);
  return (
    <Chip className="text-xxs px-2 py-1" color={(step?.color as any) ?? 'default'}>
      {step?.icon && <Icon name={step?.icon} className="mr-1" />}{step?.label ?? 'Unknown'}
    </Chip>
  );
};

export const myOffersColumns = (): ColumnDef<OfferItemList>[] => [
  {
    accessorKey: 'status',
    header: 'EOI Status',
    cell: ({ row }) => <StatusBadge status={row.original.status.name} />,
  },
  {
    accessorKey: 'name',
    header: 'Opportunity',
    cell: ({ row }) => <OpportunitiesDisplay opportunityName={row.original.opportunity.name} />,
  },
  {
    accessorKey: 'technologies',
    header: 'Technology',
    cell: ({ row }) => <TechnologyIcons technologies={row.original.opportunity.technologies} />,
  },
  {
    accessorKey: 'capacity',
    header: 'Capacity',
    cell: ({ row }) => (
      <CapacityDisplay
        capacity={row.original.opportunity.technologies.capacity}
        hasPV={row.original.opportunity.hasPV}
      />
    ),
  },
  {
    accessorKey: 'location',
    header: 'Location',
    cell: ({ row }) => <LocationDisplay location={row.original.opportunity.location} />,
  },
  {
    accessorKey: 'alerts',
    header: 'Alerts',
    cell: () => {
      //TODO: Modificar cuando vengan datos del objeto "alerts".
      const alerts: any = [];

      return (
        <div className="flex items-start">
          {alerts.length ? (
            <span className="bg-secondary-100 text-secondary-700 inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium">
              <Icon name="triangle-alert" className="h-3 w-3 text-red-900" />
              <Typography size="body-3" className="text-red-900">
                2
              </Typography>
            </span>
          ) : (
            <Typography className="text-muted-foreground" size="body-3">
              —
            </Typography>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: 'details',
    header: 'See details',
    cell: () => (
      <div className="flex w-full justify-center">
        <Icon
          name="eye-open"
          className="hover:bg-primary-700 text-primary-700 h-8 w-8 rounded-sm p-1 transition duration-200 ease-in-out hover:text-white"
        />
      </div>
    ),
  },
];
